import React, { useRef } from 'react';
import { FootballCardAttributes, FootballPosition, PreferredFoot, CardCosmeticTheme } from '../types';
import { NumberInput } from './NumberInput';
import { Sparkles, Download, Share2, Shield, Award, Trophy, Star } from 'lucide-react';

interface FootballCardProps {
  name: string;
  nickname?: string;
  position: FootballPosition;
  avatar: string;
  card: FootballCardAttributes;
  jerseyNumber?: number;
  preferredFoot?: PreferredFoot;
  clubName?: string;
  cosmeticTheme?: CardCosmeticTheme;
  isEditable?: boolean;
  onUpdateCard?: (updated: FootballCardAttributes) => void;
  compact?: boolean;
  onShare?: () => void;
}

export const FootballCard: React.FC<FootballCardProps> = ({
  name,
  nickname,
  position,
  avatar,
  card,
  jerseyNumber = 8,
  preferredFoot = 'Right',
  clubName = 'GigaBalls FC',
  cosmeticTheme = 'default',
  isEditable = false,
  onUpdateCard,
  compact = false,
  onShare,
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const isGk = position === 'GK' || (card.gkReflexes && card.gkReflexes > 0);

  // Cosmetic theme borders and subtle badges
  const getThemeStyles = () => {
    switch (cosmeticTheme) {
      case 'centurion_100':
        return {
          badge: '100 APPS',
          badgeBg: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
          borderColor: 'border-emerald-500/30',
          accentColor: 'text-emerald-400',
        };
      case 'top_scorer':
        return {
          badge: 'TOP SCORER',
          badgeBg: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
          borderColor: 'border-amber-500/30',
          accentColor: 'text-amber-400',
        };
      case 'motm_king':
        return {
          badge: 'MOTM STAR',
          badgeBg: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
          borderColor: 'border-purple-500/30',
          accentColor: 'text-purple-400',
        };
      case 'tournament_champ':
        return {
          badge: 'CHAMPION',
          badgeBg: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
          borderColor: 'border-cyan-500/30',
          accentColor: 'text-cyan-400',
        };
      default:
        return {
          badge: '',
          badgeBg: '',
          borderColor: 'border-white/10',
          accentColor: 'text-[#a8c936]',
        };
    }
  };

  const theme = getThemeStyles();

  const handleAttributeChange = (key: keyof FootballCardAttributes, val: number) => {
    if (!onUpdateCard) return;
    const clamped = Math.max(1, Math.min(99, val || 0));
    const nextCard = { ...card, [key]: clamped };
    onUpdateCard(nextCard);
  };

  if (compact) {
    return (
      <div className="relative p-3 rounded-xl bg-[#171c19] border border-white/5 shadow-md flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <img src={avatar} alt={name} className="w-10 h-10 rounded-xl object-cover border border-white/10" />
            <span className="absolute -bottom-1 -right-1 bg-[#080b0a] text-slate-300 text-[9px] font-mono font-bold px-1 py-0.2 rounded border border-white/10">
              #{jerseyNumber}
            </span>
          </div>
          <div>
            <div className="flex items-center gap-1.5">
              <span className="font-bold text-sm text-white">{name}</span>
              <span className="text-[10px] font-semibold text-[#a8c936] bg-[#a8c936]/10 px-1.5 py-0.5 rounded border border-[#a8c936]/20">
                {position}
              </span>
            </div>
            <div className="text-[11px] text-slate-400 flex gap-2 font-mono mt-0.5">
              {isGk ? (
                <>
                  <span>DIV {card.gkDiving || 70}</span>
                  <span>REF {card.gkReflexes || 75}</span>
                  <span>POS {card.gkPositioning || 70}</span>
                </>
              ) : (
                <>
                  <span>PAC {card.pace}</span>
                  <span>SHO {card.shooting}</span>
                  <span>PAS {card.passing}</span>
                  <span>DRI {card.dribbling}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="text-right pl-2">
          <div className="text-2xl font-extrabold text-white font-mono leading-none">{card.overall}</div>
          <div className="text-[9px] font-semibold text-slate-400 uppercase tracking-wider mt-0.5">OVR</div>
        </div>
      </div>
    );
  }

  // Outfield Attributes list
  const outfieldAttrs = [
    { label: 'PAC', key: 'pace', val: card.pace },
    { label: 'SHO', key: 'shooting', val: card.shooting },
    { label: 'PAS', key: 'passing', val: card.passing },
    { label: 'DRI', key: 'dribbling', val: card.dribbling },
    { label: 'DEF', key: 'defending', val: card.defending },
    { label: 'PHY', key: 'physical', val: card.physical },
  ];

  // Goalkeeper Attributes list
  const gkAttrs = [
    { label: 'DIV', key: 'gkDiving', val: card.gkDiving || 70 },
    { label: 'HAN', key: 'gkHandling', val: card.gkHandling || 70 },
    { label: 'KIC', key: 'gkKicking', val: card.gkKicking || 70 },
    { label: 'REF', key: 'gkReflexes', val: card.gkReflexes || 75 },
    { label: 'SPD', key: 'gkSpeed', val: card.gkSpeed || 60 },
    { label: 'POS', key: 'gkPositioning', val: card.gkPositioning || 70 },
  ];

  const currentAttrs = isGk ? gkAttrs : outfieldAttrs;

  return (
    <div
      ref={cardRef}
      className={`relative w-full max-w-[320px] mx-auto rounded-2xl p-5 bg-[#121614] border ${theme.borderColor} shadow-2xl overflow-hidden`}
    >
      {/* Background Subtle Gradient & Watermark */}
      <div className="absolute -top-16 -right-16 w-48 h-48 bg-[#a8c936]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-2 right-2 opacity-5 pointer-events-none text-white text-6xl font-black font-mono">
        {position}
      </div>

      {/* Top Header: Badge, Share button */}
      <div className="flex items-center justify-between mb-3 relative z-10">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-bold text-white tracking-wide">{clubName}</span>
          {theme.badge && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${theme.badgeBg}`}>
              {theme.badge}
            </span>
          )}
        </div>
        {onShare && (
          <button
            onClick={onShare}
            type="button"
            className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 hover:text-white transition"
            title="Share Player Card"
          >
            <Share2 className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Main Card Body: Player Photo + Rating & Pos */}
      <div className="relative z-10 flex items-center gap-4 bg-[#171c19] p-3.5 rounded-xl border border-white/5 mb-4">
        <div className="relative w-20 h-24 rounded-xl overflow-hidden border border-white/10 bg-[#080b0a] shrink-0">
          <img src={avatar} alt={name} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        </div>

        <div className="flex-1 flex flex-col justify-center">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-white font-mono leading-none tracking-tight">
              {card.overall}
            </span>
            <span className="text-xs font-bold text-[#a8c936] uppercase tracking-wider">
              {position}
            </span>
          </div>

          <h3 className="text-base font-bold text-white leading-tight mt-1 truncate">
            {name}
          </h3>
          {nickname && (
            <span className="text-xs text-slate-400 font-medium truncate block">"{nickname}"</span>
          )}

          <div className="flex items-center gap-2 mt-2 text-[11px] text-slate-400 font-mono">
            <span>#{jerseyNumber}</span>
            <span>•</span>
            <span>{preferredFoot} Foot</span>
          </div>
        </div>
      </div>

      {/* Compact Attributes Grid */}
      <div className="relative z-10 grid grid-cols-2 gap-2">
        {currentAttrs.map((item) => (
          <div
            key={item.key}
            className="flex justify-between items-center bg-[#171c19] px-3 py-2 rounded-xl border border-white/5"
          >
            <span className="text-xs font-medium text-slate-400">{item.label}</span>
            {isEditable ? (
              <NumberInput
                value={item.val}
                onCommit={(v) => handleAttributeChange(item.key as keyof FootballCardAttributes, v)}
                min={1}
                max={99}
                emptyDefault={60}
                className="w-10 bg-[#080b0a] text-white border border-white/10 rounded px-1 text-center font-mono text-xs focus:outline-none focus:border-[#a8c936]"
              />
            ) : (
              <span className="font-bold text-xs text-white font-mono">{item.val}</span>
            )}
          </div>
        ))}
      </div>

      {/* Footer Branding */}
      <div className="relative z-10 mt-4 pt-3 border-t border-white/5 flex items-center justify-between text-[11px] text-slate-400">
        <span>Official Football Card</span>
        <span className="font-mono text-slate-400">2026</span>
      </div>
    </div>
  );
};
